import type { ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useMemo } from 'react'
import type { MockView } from '../api/mocks'
import { longDate } from '../format'
import { defaultColDef, gridTheme } from '../grid'

export interface MocksGridProps {
  rows: MockView[]
  pending: boolean
  onOpen: (code: string) => void
}

/**
 * Every Mock of the Exam, from the shared grid theme and defaults; a click opens one.
 *
 * Its columns fit a 1280px window's page (about 1,160px of grid): the fixed ones are as wide
 * as their heading and filter need, and Title and Published share what is left. Wider fixed
 * columns pushed Published past the grid's edge, behind a scrollbar that covered the last row
 * (ADMIN-15).
 */
export function MocksGrid({ rows, pending, onOpen }: MocksGridProps) {
  const columns = useMemo<ColDef<MockView>[]>(
    () => [
      { field: 'code', headerName: 'Mock', width: 170, pinned: 'left', cellClass: 'font-bold' },
      { field: 'title', headerName: 'Title', flex: 1, minWidth: 150 },
      { field: 'kind', headerName: 'Kind', width: 110 },
      { field: 'status', headerName: 'Status', width: 120 },
      {
        colId: 'session',
        headerName: 'Session',
        width: 130,
        valueGetter: (row) =>
          row.data && (row.data.session === 'Default' ? row.data.componentCode : `${row.data.componentCode} ${row.data.session}`),
      },
      { colId: 'questions', headerName: 'Questions', width: 120, valueGetter: (row) => row.data?.entries.length },
      {
        field: 'countsTowardsDailyLimit',
        headerName: 'Spends the day',
        width: 160,
        valueFormatter: (cell) => (cell.value ? 'Yes' : 'No'),
      },
      {
        field: 'publishedAt',
        headerName: 'Published',
        flex: 1,
        minWidth: 160,
        valueFormatter: (cell) => (cell.value ? longDate(cell.value.slice(0, 10)) : ''),
      },
    ],
    [],
  )

  return (
    <div className="mt-4 w-full">
      <AgGridReact<MockView>
        theme={gridTheme}
        domLayout="autoHeight"
        columnDefs={columns}
        defaultColDef={defaultColDef}
        rowData={pending ? undefined : rows}
        loading={pending}
        getRowId={(row) => row.data.code}
        overlayNoRowsTemplate="No Mock built yet."
        onRowClicked={(row) => {
          if (row.data) onOpen(row.data.code)
        }}
      />
    </div>
  )
}
